import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Zap } from "lucide-react";
import { getStreak } from "@/lib/api";
import { useEnergia } from "@/hooks/useEnergia";

/**
 * StreakWidget — "Streak Charge" del Alpha Journey.
 *
 * Píldora en el Header con un rayo y el contador de la racha.
 *  - done (actividad de hoy hecha)  → rojo de la casa #ce1212, rayo relleno, glow.
 *  - at_risk (racha viva, hoy pendiente) → gris apagado, pulso sutil de aviso.
 *  - off (sin racha)               → gris muy apagado.
 * Al pasar el ratón / tocar, despliega una tarjeta oscura con la semana y las cargas.
 *
 * Escucha el evento global "alpha:streak" para refrescarse cuando otra parte de la
 * app registre actividad (record_activity devuelve la racha nueva).
 */

const DONE = "#ce1212";

export default function StreakWidget() {
  // Lo que hoy se le pide, según lo que él mismo dijo al entrar. Ver `lib/energia.mjs`.
  const [, , pideEnergia] = useEnergia();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const load = async () => {
    try {
      setData(await getStreak());
    } catch {
      /* silencioso: sin sesión o backend caído */
    }
  };

  useEffect(() => {
    load();
    // Otras vistas emiten este evento tras completar lección / diálogo / repaso.
    const onStreak = (e) => {
      if (e.detail) setData(e.detail);
      else load();
    };
    window.addEventListener("alpha:streak", onStreak);
    return () => window.removeEventListener("alpha:streak", onStreak);
  }, []);

  if (!data) return null;

  const { current, best, charges, state, week } = data;
  const done = state === "done";
  const atRisk = state === "at_risk";

  const color = done ? DONE : atRisk ? "#8a8a8a" : "#5a5a5a";

  const openNow = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  return (
    <div
      className="relative"
      onMouseEnter={openNow}
      onMouseLeave={closeSoon}
      data-testid="streak-widget"
    >
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label={`Racha: ${current} días`}
        className="relative inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-colors"
        style={{
          borderColor: done ? "rgba(206,18,18,0.35)" : "rgba(0,0,0,0.12)",
          background: done ? "rgba(206,18,18,0.08)" : "transparent",
        }}
        data-testid="streak-pill"
      >
        <motion.span
          animate={atRisk ? { opacity: [1, 0.45, 1] } : { opacity: 1 }}
          transition={atRisk ? { duration: 1.6, repeat: Infinity } : { duration: 0 }}
          className="inline-flex"
        >
          <Zap
            className="w-4 h-4"
            style={{ color }}
            fill={done ? DONE : "none"}
            strokeWidth={done ? 1.5 : 2}
          />
        </motion.span>
        <span
          className="font-mono-alpha text-[13px] font-semibold tabular-nums leading-none"
          style={{ color }}
        >
          {current}
        </span>
        {charges > 0 && (
          <span
            className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-mono-alpha font-bold"
            style={{ background: "rgba(206,18,18,0.15)", color: DONE }}
            title={`${charges} carga(s) de racha`}
          >
            ⚡{charges}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.16 }}
            className="absolute right-0 top-full pt-2 w-72 z-50"
            data-testid="streak-popover"
          >
            <div className="bg-[#0b0b0b] text-white rounded-2xl border border-[#222] shadow-2xl overflow-hidden">
              <div className="p-5 pb-4 border-b border-[#1c1c1c]">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-mono-alpha text-[10px] uppercase tracking-widest text-white/40">
                      Tu racha
                    </div>
                    <div className="flex items-baseline gap-1.5 mt-1">
                      <span
                        className="font-serif-display text-4xl leading-none"
                        style={{ color: done ? DONE : "#fff" }}
                      >
                        {current}
                      </span>
                      <span className="text-white/50 text-sm">
                        día{current === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-alpha text-[10px] uppercase tracking-widest text-white/40">
                      Récord
                    </div>
                    <div className="font-serif-display text-2xl mt-1 text-white/80">
                      {best}
                    </div>
                  </div>
                </div>
                <div className="mt-3 text-[12px] leading-snug">
                  {done && (
                    <span style={{ color: DONE }}>
                      ¡Actividad de hoy completada! Sigue así.
                    </span>
                  )}
                  {/* CON POCA BATERÍA NO SE INSISTE (P5). La frase se queda, porque quitarla
                      sería esconder el estado de su propia racha; lo que se va es la prisa.
                      «Aún no has entrenado» a alguien que ha dicho que hoy viene justo es
                      exactamente lo que la regla prohíbe: subir la exigencia a quien acaba de
                      decir que la tiene baja. */}
                  {atRisk && pideEnergia.insiste && (
                    <span className="text-white/60">
                      Tu racha está viva pero hoy aún no has entrenado.
                    </span>
                  )}
                  {atRisk && !pideEnergia.insiste && (
                    <span className="text-white/50" data-testid="racha-sin-prisa">
                      Tu racha sigue viva. Con una lección corta basta para mantenerla.
                    </span>
                  )}
                  {state === "off" && (
                    <span className="text-white/50">
                      Empieza hoy una lección o un diálogo para encender tu racha.
                    </span>
                  )}
                </div>
              </div>

              <div className="p-5 pt-4">
                <div className="flex items-center justify-between">
                  {week.map((d) => (
                    <div key={d.date} className="flex flex-col items-center gap-1.5">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-mono-alpha transition-colors ${
                          d.is_today ? "ring-1 ring-white/30" : ""
                        }`}
                        style={{
                          background: d.done ? DONE : "#1a1a1a",
                          color: d.done ? "#fff" : "rgba(255,255,255,0.35)",
                        }}
                      >
                        {d.done ? <Zap className="w-3.5 h-3.5" fill="#fff" strokeWidth={0} /> : ""}
                      </div>
                      <span
                        className={`text-[10px] font-mono-alpha ${
                          d.is_today ? "text-white/80" : "text-white/30"
                        }`}
                      >
                        {d.label}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-4 flex items-center gap-2 rounded-xl bg-[#121212] border border-[#1c1c1c] px-3 py-2.5">
                  <Zap className="w-4 h-4" style={{ color: DONE }} fill={DONE} strokeWidth={0} />
                  <div className="text-[12px] text-white/70 leading-tight">
                    <span className="text-white font-semibold">{charges}</span> carga
                    {charges === 1 ? "" : "s"} de racha
                    <span className="block text-[10px] text-white/40">
                      Cada carga salva un día que te saltes.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
